import { useState } from "react";
import { Upload, Link, Loader2, Check, X, ImageIcon, Globe, ChevronDown, ChevronUp } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface ExtractedAddon {
  group_name: string;
  is_required?: boolean;
  min_select?: number;
  max_select?: number;
  items: { name: string; price: number }[];
}

interface ExtractedProduct {
  name: string;
  description?: string;
  price: number;
  discount_price?: number;
  is_featured?: boolean;
  category?: string;
  image_url?: string;
  addons?: ExtractedAddon[];
  _selected?: boolean;
}

interface ImportMenuDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  restaurantId: string;
  onImportComplete: () => void;
}

export const ImportMenuDialog = ({ open, onOpenChange, restaurantId, onImportComplete }: ImportMenuDialogProps) => {
  const { toast } = useToast();
  const [mode, setMode] = useState<"image" | "url" | null>(null);
  const [url, setUrl] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [products, setProducts] = useState<ExtractedProduct[]>([]);
  const [importing, setImporting] = useState(false);
  const [expandedProduct, setExpandedProduct] = useState<number | null>(null);

  const reset = () => {
    setMode(null);
    setUrl("");
    setImageFile(null);
    setImagePreview(null);
    setProducts([]);
    setExpandedProduct(null);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "Imagem muito grande", description: "Máximo 10MB", variant: "destructive" });
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(",")[1]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleExtract = async () => {
    setExtracting(true);
    try {
      const body: any = { mode };

      if (mode === "url") {
        if (!url.trim()) {
          toast({ title: "Informe a URL", variant: "destructive" });
          setExtracting(false);
          return;
        }
        body.url = url.trim();
      } else {
        if (!imageFile) {
          toast({ title: "Selecione uma imagem", variant: "destructive" });
          setExtracting(false);
          return;
        }
        body.image_base64 = await fileToBase64(imageFile);
        body.image_mime_type = imageFile.type;
      }

      const { data, error } = await supabase.functions.invoke("import-menu", { body });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Erro na extração");

      const extracted = (data.data.products || []).map((p: any) => ({ ...p, _selected: true }));
      if (extracted.length === 0) {
        toast({ title: "Nenhum produto encontrado", description: "Tente com outra imagem ou URL", variant: "destructive" });
      } else {
        setProducts(extracted);
        toast({ title: `${extracted.length} produtos encontrados!` });
      }
    } catch (err: any) {
      console.error("Extract error:", err);
      toast({ title: "Erro na extração", description: err.message || "Tente novamente", variant: "destructive" });
    } finally {
      setExtracting(false);
    }
  };

  const toggleProduct = (idx: number) => {
    setProducts(prev => prev.map((p, i) => i === idx ? { ...p, _selected: !p._selected } : p));
  };

  const toggleAll = (selected: boolean) => {
    setProducts(prev => prev.map(p => ({ ...p, _selected: selected })));
  };

  const handleImport = async () => {
    const selected = products.filter(p => p._selected);
    if (selected.length === 0) {
      toast({ title: "Selecione pelo menos um produto", variant: "destructive" });
      return;
    }

    setImporting(true);
    try {
      // Group by category
      const categoryNames = [...new Set(selected.map(p => p.category).filter(Boolean))] as string[];

      // Get existing categories
      const { data: existingCats } = await supabase
        .from("categories")
        .select("id, name")
        .eq("restaurant_id", restaurantId);

      const catMap: Record<string, string> = {};
      (existingCats || []).forEach(c => { catMap[c.name.toLowerCase()] = c.id; });

      // Create missing categories
      for (const catName of categoryNames) {
        if (!catMap[catName.toLowerCase()]) {
          const { data: newCat } = await supabase
            .from("categories")
            .insert({ restaurant_id: restaurantId, name: catName, sort_order: Object.keys(catMap).length })
            .select("id")
            .single();
          if (newCat) catMap[catName.toLowerCase()] = newCat.id;
        }
      }

      // Get current product count for sort_order
      const { count: prodCount } = await supabase
        .from("products")
        .select("id", { count: "exact", head: true })
        .eq("restaurant_id", restaurantId);

      let sortOrder = prodCount || 0;

      for (const prod of selected) {
        const categoryId = prod.category ? catMap[prod.category.toLowerCase()] || null : null;

        const { data: newProd, error: prodError } = await supabase
          .from("products")
          .insert({
            restaurant_id: restaurantId,
            name: prod.name,
            description: prod.description || null,
            price: prod.price,
            discount_price: prod.discount_price || null,
            category_id: categoryId,
            image_url: prod.image_url || null,
            is_featured: prod.is_featured || false,
            is_available: true,
            sort_order: sortOrder++,
          })
          .select("id")
          .single();

        if (prodError || !newProd) {
          console.error("Error creating product:", prodError);
          continue;
        }

        // Create addons
        if (prod.addons && prod.addons.length > 0) {
          for (let gi = 0; gi < prod.addons.length; gi++) {
            const group = prod.addons[gi];
            const { data: newCat } = await supabase
              .from("addon_categories" as any)
              .insert({
                product_id: newProd.id,
                restaurant_id: restaurantId,
                name: group.group_name,
                is_required: group.is_required || false,
                min_select: group.min_select || 0,
                max_select: group.max_select || (group.items.length > 1 ? group.items.length : 1),
                sort_order: gi,
              })
              .select("id")
              .single();

            if (!newCat) continue;
            const catId = (newCat as any).id;

            for (let ai = 0; ai < group.items.length; ai++) {
              const addon = group.items[ai];
              await supabase.from("product_addons").insert({
                product_id: newProd.id,
                addon_category_id: catId,
                name: addon.name,
                price: addon.price || 0,
                is_available: true,
                sort_order: ai,
                min_qty: 0,
                max_qty: 1,
              });
            }
          }
        }
      }

      toast({ title: `${selected.length} produtos importados com sucesso!` });
      onImportComplete();
      onOpenChange(false);
      reset();
    } catch (err: any) {
      console.error("Import error:", err);
      toast({ title: "Erro ao importar", description: err.message, variant: "destructive" });
    } finally {
      setImporting(false);
    }
  };

  const selectedCount = products.filter(p => p._selected).length;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Upload className="h-5 w-5 text-primary" />
            Importar Cardápio
          </DialogTitle>
        </DialogHeader>

        {/* Step 1: Choose mode */}
        {!mode && products.length === 0 && (
          <div className="grid grid-cols-2 gap-4 py-4">
            <button
              onClick={() => setMode("image")}
              className="flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-dashed border-border hover:border-primary hover:bg-primary/5 transition-all group"
            >
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <ImageIcon className="h-7 w-7 text-primary" />
              </div>
              <div className="text-center">
                <p className="font-semibold text-sm text-foreground">Cardápio Físico</p>
                <p className="text-xs text-muted-foreground mt-1">Envie uma foto do cardápio</p>
              </div>
            </button>

            <button
              onClick={() => setMode("url")}
              className="flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-dashed border-border hover:border-primary hover:bg-primary/5 transition-all group"
            >
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <Globe className="h-7 w-7 text-primary" />
              </div>
              <div className="text-center">
                <p className="font-semibold text-sm text-foreground">Cardápio Digital</p>
                <p className="text-xs text-muted-foreground mt-1">Cole a URL do menu online</p>
              </div>
            </button>
          </div>
        )}

        {/* Step 2: Input */}
        {mode && products.length === 0 && (
          <div className="space-y-4 py-2">
            <Button variant="ghost" size="sm" onClick={() => setMode(null)} className="text-xs text-muted-foreground">
              ← Voltar
            </Button>

            {mode === "url" ? (
              <div className="space-y-2">
                <Label>URL do cardápio digital</Label>
                <Input
                  placeholder="https://exemplo.com/cardapio"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="bg-background border-border"
                />
                <p className="text-xs text-muted-foreground">
                  Cole o link do menu digital que deseja importar
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Foto do cardápio</Label>
                {imagePreview ? (
                  <div className="relative rounded-xl overflow-hidden border border-border">
                    <img src={imagePreview} alt="Preview" className="w-full max-h-64 object-contain bg-secondary/20" />
                    <button
                      onClick={() => { setImageFile(null); setImagePreview(null); }}
                      className="absolute top-2 right-2 p-1.5 rounded-full bg-background/80 hover:bg-background"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center gap-3 p-8 rounded-xl border-2 border-dashed border-border cursor-pointer hover:border-primary hover:bg-primary/5 transition-all">
                    <Upload className="h-8 w-8 text-muted-foreground" />
                    <div className="text-center">
                      <p className="text-sm font-medium text-foreground">Clique para enviar</p>
                      <p className="text-xs text-muted-foreground">JPG, PNG até 10MB</p>
                    </div>
                    <input type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
                  </label>
                )}
              </div>
            )}

            <Button
              onClick={handleExtract}
              disabled={extracting || (mode === "url" ? !url.trim() : !imageFile)}
              className="w-full rounded-xl gap-2"
            >
              {extracting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Extraindo produtos...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Extrair Produtos
                </>
              )}
            </Button>
          </div>
        )}

        {/* Step 3: Review products */}
        {products.length > 0 && (
          <div className="flex flex-col flex-1 min-h-0 gap-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {selectedCount} de {products.length} selecionados
              </p>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" className="text-xs" onClick={() => toggleAll(true)}>
                  Selecionar todos
                </Button>
                <Button variant="ghost" size="sm" className="text-xs" onClick={() => toggleAll(false)}>
                  Limpar
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto space-y-2 pr-1 max-h-[50vh]">
              {products.map((prod, idx) => (
                <div
                  key={idx}
                  className={`rounded-xl border transition-all ${
                    prod._selected ? "border-primary/40 bg-primary/5" : "border-border bg-card"
                  }`}
                >
                  <div className="flex items-center gap-3 p-3">
                    <Checkbox
                      checked={prod._selected}
                      onCheckedChange={() => toggleProduct(idx)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium truncate">{prod.name}</p>
                        <div className="flex items-center gap-2 shrink-0">
                          {prod.discount_price ? (
                            <div className="text-right">
                              <span className="text-sm font-bold text-primary">
                                R$ {prod.discount_price.toFixed(2).replace(".", ",")}
                              </span>
                              <span className="text-xs text-muted-foreground line-through ml-1">
                                R$ {prod.price.toFixed(2).replace(".", ",")}
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm font-bold text-primary">
                              R$ {prod.price.toFixed(2).replace(".", ",")}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 mt-1">
                        {prod.category && (
                          <Badge variant="secondary" className="text-[10px] h-4 px-1.5">{prod.category}</Badge>
                        )}
                        {prod.addons && prod.addons.length > 0 && (
                          <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                            {prod.addons.reduce((a, g) => a + g.items.length, 0)} adicionais
                          </Badge>
                        )}
                        {prod.is_featured && (
                          <Badge className="text-[10px] h-4 px-1.5 bg-amber-500/90 text-white border-0">Destaque</Badge>
                        )}
                      </div>
                    </div>
                    {prod.addons && prod.addons.length > 0 && (
                      <button onClick={() => setExpandedProduct(expandedProduct === idx ? null : idx)}>
                        {expandedProduct === idx ? (
                          <ChevronUp className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        )}
                      </button>
                    )}
                  </div>

                  {/* Expanded addon details */}
                  {expandedProduct === idx && prod.addons && (
                    <div className="px-3 pb-3 pt-0 border-t border-border/40 mt-0">
                      <div className="space-y-2 mt-2">
                        {prod.description && (
                          <p className="text-xs text-muted-foreground">{prod.description}</p>
                        )}
                        {prod.addons.map((group, gi) => (
                          <div key={gi} className="bg-secondary/30 rounded-lg p-2">
                            <p className="text-xs font-medium text-foreground flex items-center gap-1">
                              {group.group_name}
                              {group.is_required && (
                                <Badge variant="destructive" className="text-[8px] h-3.5 px-1">Obrigatório</Badge>
                              )}
                            </p>
                            <div className="mt-1 space-y-0.5">
                              {group.items.map((item, ai) => (
                                <div key={ai} className="flex items-center justify-between text-xs text-muted-foreground">
                                  <span>{item.name}</span>
                                  {item.price > 0 && (
                                    <span>+ R$ {item.price.toFixed(2).replace(".", ",")}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex gap-3 pt-2 border-t border-border/40">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={reset}>
                Voltar
              </Button>
              <Button
                className="flex-1 rounded-xl gap-2"
                onClick={handleImport}
                disabled={importing || selectedCount === 0}
              >
                {importing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Importando...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4" />
                    Importar {selectedCount} produtos
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
